/**
 * Integrações › **IA: OpenRouter e transcrição própria** (D631, doc 25 section 11; owner answers Q-362 and Q-363). The AI provider credentials live in
 * the same vault as every other provider key - never in a setting, never in a log:
 *  - the OpenRouter API key (WRITE-ONLY: a masked hint and a configured badge, never the key) and its on/off switch;
 *  - the self-hosted speech endpoints (the owner's GPU server): a base URL and an optional bearer key (write-only), saved under a credential NAME.
 *    The governed rule "Atendimento e IA" points at that name (`ai.speechEngine`); it carries neither the URL nor the key.
 * Which MODEL each purpose uses is that governed rule, not this panel. Composes `@vello/web-theme` only.
 */
import { type FormEvent, type ReactElement, useEffect, useId, useState } from 'react';
import type { AiCredentialsView, AiOpenrouterSlotView, AiSpeechSlotView } from '@vello/api-client';
import { Badge, Button, Divided, Field, FormGrid, FormStack, Notice, Panel, Row, Select, Stack, Strong, Text, TextInput } from '@vello/web-theme';
import { speechCredentialRefOf } from '../../src/index';
import { useAdmin } from '../session';
import { useT } from '../i18n/provider';
import { useVocab } from '../i18n/vocab';

type Message = { readonly tone: 'success' | 'danger'; readonly text: string } | undefined;

function OpenrouterSlot({ view, onSaved }: { view: AiOpenrouterSlotView; onSaved: (next: AiOpenrouterSlotView) => void }): ReactElement {
  const { runtime } = useAdmin();
  const { t } = useT();
  const { fail, explain } = useVocab();
  const id = useId();
  const [enabled, setEnabled] = useState(view.enabled);
  const [apiKey, setApiKey] = useState('');
  const [msg, setMsg] = useState<Message>(undefined);
  const [busy, setBusy] = useState(false);

  const onSave = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setMsg(undefined);
    setBusy(true);
    const res = await runtime.integrations.setAiOpenrouterSlot({ enabled, ...(apiKey.trim() !== '' ? { apiKey: apiKey.trim() } : {}) });
    setBusy(false);
    if (res.ok) {
      onSaved(res.value);
      setEnabled(res.value.enabled);
      setApiKey('');
      setMsg({ tone: 'success', text: t('aiint.saved') });
    } else {
      setMsg({ tone: 'danger', text: explain(res.error) ?? t('aiint.save.failed', { code: fail(res.error) }) });
    }
  };

  return (
    <FormStack divided onSubmit={(e) => void onSave(e)} noValidate aria-labelledby={`${id}-h`}>
      <Row gap={3} align="baseline" wrap>
        <Strong as="h3" role="body" id={`${id}-h`}>{t('aiint.openrouter')}</Strong>
        <Badge tone={view.configured ? 'success' : 'warning'}>{view.configured ? t('int.status.configured') : t('int.status.notConfigured')}</Badge>
        {view.enabled && <Badge tone="success">{t('int.status.enabled')}</Badge>}
      </Row>
      <FormGrid>
        <Field label={t('geoint.field.enabled')} htmlFor={`${id}-enabled`}>
          <Select id={`${id}-enabled`} value={enabled ? 'on' : 'off'} onChange={(e) => setEnabled(e.target.value === 'on')}>
            <option value="on">{t('geoint.field.enabled.on')}</option>
            <option value="off">{t('geoint.field.enabled.off')}</option>
          </Select>
        </Field>
        <Field label={t('geoint.field.apiKey')} htmlFor={`${id}-key`} hint={view.configured ? t('geoint.field.apiKey.hintConfigured', { hint: view.maskedHint ?? '' }) : t('geoint.field.apiKey.hint')}>
          <TextInput id={`${id}-key`} type="password" autoComplete="off" value={apiKey} onChange={(e) => setApiKey(e.target.value)} />
        </Field>
      </FormGrid>
      {msg !== undefined && <div><Notice tone={msg.tone}>{msg.text}</Notice></div>}
      <div><Button type="submit" variant="secondary" disabled={busy}>{busy ? t('geoint.saving') : t('geoint.save')}</Button></div>
    </FormStack>
  );
}

/** One self-hosted speech endpoint. `fixedRef` is set for a known endpoint; an empty one asks for the credential name. */
function SpeechSlot({ credentialRef: fixedRef, view, onSaved }: { credentialRef: string | undefined; view: AiSpeechSlotView | undefined; onSaved: (next: AiSpeechSlotView) => void }): ReactElement {
  const { runtime } = useAdmin();
  const { t } = useT();
  const { fail, explain } = useVocab();
  const id = useId();
  const [name, setName] = useState(fixedRef ?? '');
  const [enabled, setEnabled] = useState(view?.enabled ?? false);
  const [baseUrl, setBaseUrl] = useState(view?.baseUrl ?? '');
  const [apiKey, setApiKey] = useState('');
  const [msg, setMsg] = useState<Message>(undefined);
  const [busy, setBusy] = useState(false);
  const ref = (fixedRef ?? name).trim();

  const onSave = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setMsg(undefined);
    setBusy(true);
    const res = await runtime.integrations.setAiSpeechSlot(ref, { enabled, ...(baseUrl.trim() !== '' ? { baseUrl: baseUrl.trim() } : {}), ...(apiKey.trim() !== '' ? { apiKey: apiKey.trim() } : {}) });
    setBusy(false);
    if (res.ok) {
      onSaved(res.value);
      setApiKey('');
      setMsg({ tone: 'success', text: t('aiint.saved') });
    } else {
      setMsg({ tone: 'danger', text: explain(res.error) ?? t('aiint.save.failed', { code: fail(res.error) }) });
    }
  };

  return (
    <FormStack divided onSubmit={(e) => void onSave(e)} noValidate aria-labelledby={`${id}-h`}>
      <Row gap={3} align="baseline" wrap>
        <Strong as="h3" role="body" id={`${id}-h`}>{fixedRef !== undefined ? t('aiint.speech.named', { name: fixedRef }) : t('aiint.speech.new')}</Strong>
        {view !== undefined && <Badge tone={view.configured ? 'success' : 'warning'}>{view.configured ? t('int.status.configured') : t('int.status.notConfigured')}</Badge>}
        {view?.enabled === true && <Badge tone="success">{t('int.status.enabled')}</Badge>}
      </Row>
      <FormGrid>
        {fixedRef === undefined && (
          <Field label={t('aiint.speech.name')} htmlFor={`${id}-name`} hint={t('aiint.speech.name.hint')}>
            <TextInput id={`${id}-name`} autoComplete="off" spellCheck={false} maxLength={41} value={name} onChange={(e) => setName(e.target.value.trim())} />
          </Field>
        )}
        <Field label={t('geoint.field.enabled')} htmlFor={`${id}-enabled`}>
          <Select id={`${id}-enabled`} value={enabled ? 'on' : 'off'} onChange={(e) => setEnabled(e.target.value === 'on')}>
            <option value="on">{t('geoint.field.enabled.on')}</option>
            <option value="off">{t('geoint.field.enabled.off')}</option>
          </Select>
        </Field>
        <Field label={t('geoint.field.baseUrl')} htmlFor={`${id}-url`} hint={t('geoint.field.baseUrl.hint')}>
          <TextInput id={`${id}-url`} type="url" inputMode="url" autoComplete="off" value={baseUrl} onChange={(e) => setBaseUrl(e.target.value)} />
        </Field>
        <Field label={t('aiint.speech.key')} htmlFor={`${id}-key`} hint={view?.hasKey === true ? t('geoint.field.apiKey.hintConfigured', { hint: view.maskedHint ?? '' }) : t('aiint.speech.key.hint')}>
          <TextInput id={`${id}-key`} type="password" autoComplete="off" value={apiKey} onChange={(e) => setApiKey(e.target.value)} />
        </Field>
      </FormGrid>
      {msg !== undefined && <div><Notice tone={msg.tone}>{msg.text}</Notice></div>}
      <div><Button type="submit" variant="secondary" disabled={busy || ref === ''}>{busy ? t('geoint.saving') : t('geoint.save')}</Button></div>
    </FormStack>
  );
}

/** The whole panel: the OpenRouter key and the self-hosted speech endpoints the country settings name, plus a new one. */
export function AiProvidersPanel(): ReactElement {
  const { runtime } = useAdmin();
  const { t } = useT();
  const { fail } = useVocab();
  const [view, setView] = useState<AiCredentialsView | undefined>(undefined);
  const [loadMsg, setLoadMsg] = useState<string | undefined>(undefined);
  // Added during this visit (after a save): they stay listed even though no country setting names them yet.
  const [extraRefs, setExtraRefs] = useState<readonly string[]>([]);

  useEffect(() => {
    let alive = true;
    void (async () => {
      // The endpoints worth showing are the ones a country's speech-engine setting names; an operator without that read still gets the key slot.
      const settings = await runtime.settings.get('ai.speechEngine');
      const named = settings.ok ? settings.value.buckets.map((b) => speechCredentialRefOf(b.current?.value)).filter((r): r is string => r !== undefined) : [];
      const refs = [...new Set(named)];
      const res = await runtime.integrations.getAiCredentials(refs);
      if (!alive) return;
      if (res.ok) setView(res.value);
      else setLoadMsg(t('aiint.load.failed', { code: fail(res.error) }));
    })();
    return () => {
      alive = false;
    };
  }, [runtime, t, fail]);

  const speech = [...(view?.speech ?? []), ...extraRefs.filter((r) => !(view?.speech ?? []).some((s) => s.ref === r)).map((ref): AiSpeechSlotView => ({ slot: 'speech', ref, integration: '', provider: '', configured: false, enabled: false, hasKey: false }))];

  return (
    <Panel>
      <Stack gap={4}>
        <Text as="h2" role="title">{t('aiint.heading')}</Text>
        <Text as="p" role="body" tone="secondary">{t('aiint.hint')}</Text>
        {loadMsg !== undefined && <Notice tone="danger">{loadMsg}</Notice>}
        {view !== undefined && <OpenrouterSlot view={view.openrouter} onSaved={(next) => setView((cur) => (cur === undefined ? cur : { ...cur, openrouter: next }))} />}
        {view !== undefined && (
          <Divided>
            <Strong as="h3" role="body">{t('aiint.speech.heading')}</Strong>
            <Text as="p" role="caption" tone="secondary">{t('aiint.speech.hint')}</Text>
            {speech.map((s) => (
              <SpeechSlot
                key={s.ref}
                credentialRef={s.ref}
                view={s}
                onSaved={(next) => setView((cur) => (cur === undefined ? cur : { ...cur, speech: [...cur.speech.filter((x) => x.ref !== next.ref), next] }))}
              />
            ))}
            <SpeechSlot
              key={`new-${String(speech.length)}`}
              credentialRef={undefined}
              view={undefined}
              onSaved={(next) => {
                setExtraRefs((cur) => [...cur, next.ref]);
                setView((cur) => (cur === undefined ? cur : { ...cur, speech: [...cur.speech.filter((x) => x.ref !== next.ref), next] }));
              }}
            />
          </Divided>
        )}
      </Stack>
    </Panel>
  );
}
